import { z } from 'zod';
import { emailSchema, objectIdSchema } from './common.js';

/**
 * Single-use booking links.
 *
 * A host replying to an enquiry sends one of these instead of a public page.
 * It carries a code nobody can guess, works once, and expires, which makes the
 * event type unreachable to anything that has not been invited.
 */
export const MAX_INVITE_DAYS = 90;
export const DEFAULT_INVITE_DAYS = 14;

export const createInviteInputSchema = z.object({
  eventTypeId: objectIdSchema,
  /** Pre-fills the booking form; the booker can still correct it. */
  name: z.string().trim().max(120).optional(),
  email: emailSchema.optional(),
  expiresInDays: z.number().int().min(1).max(MAX_INVITE_DAYS).default(DEFAULT_INVITE_DAYS),
});
export type CreateInviteInput = z.infer<typeof createInviteInputSchema>;

export const inviteSchema = z.object({
  id: z.string(),
  eventTypeId: z.string(),
  /** Only returned when the link is minted; it is stored hashed. */
  url: z.string().nullable(),
  name: z.string().nullable(),
  email: z.string().nullable(),
  expiresAt: z.string().datetime(),
  usedAt: z.string().datetime().nullable(),
  bookingId: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export type Invite = z.infer<typeof inviteSchema>;

export const inviteListSchema = z.object({ items: z.array(inviteSchema) });

/** Why a link did not open, so the page can say something better than "not found". */
export const inviteStateSchema = z.enum(['ok', 'used', 'expired', 'unknown']);
export type InviteState = z.infer<typeof inviteStateSchema>;
